/**
 * Las fechas largas, escritas igual en todo el panel y en el idioma de quien mira.
 *
 * ── Por qué se componen a mano ──────────────────────────────────────────────
 * `Intl` en español da «sábado, 29 de agosto de 2026», todo en minúsculas. El
 * panel lo arreglaba con `text-transform: capitalize`, que capitaliza CADA
 * palabra y producía «Sábado, 29 De Agosto De 2026»: los conectores en mayúscula
 * no son un descuido de estilo, son una falta de ortografía repetida en cada
 * pantalla que enseña una fecha. Se compone pieza a pieza con `formatToParts`,
 * que es lo único que permite tocar el mes dejando «de» y «del» como deben ir.
 *
 * ── Pero eso son reglas del CASTELLANO, no de las fechas ────────────────────
 * En inglés `Intl` ya devuelve «Saturday, August 29, 2026» con sus mayúsculas y
 * sin conectores que corregir: aplicarle estas reglas no lo mejora, y la de
 * «del» le metería una palabra española dentro. Por eso cada función se corta
 * antes de arreglar nada cuando el idioma no es el castellano. Traducir no es
 * sustituir cadenas — a veces es saber qué regla deja de aplicar.
 *
 * Es el mismo criterio que `apps/worker/src/services/dates.ts`. Son dos runtimes
 * sin un paquete común para esto; si aparece un tercero, toca sacarlo a uno.
 *
 * El idioma sale de `activeLanguage()` y no de un parámetro porque esto se llama
 * desde celdas de tabla y funciones sueltas que no son componentes.
 */
import { activeLanguage } from './i18n'

/** El locale de `Intl` para el idioma vigente del panel. */
function locale(): string {
  return activeLanguage()
}

const capitalizar = (texto: string, tag: string) =>
  texto.charAt(0).toLocaleUpperCase(tag) + texto.slice(1)

/** `29 de Agosto del 2026` · `August 29, 2026`. */
export function longDate(value: string | number | Date, timeZone?: string): string {
  const tag = locale()
  const opciones: Intl.DateTimeFormatOptions = {
    ...(timeZone ? { timeZone } : {}),
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }
  const fecha = new Date(value)
  if (!tag.startsWith('es')) return new Intl.DateTimeFormat(tag, opciones).format(fecha)

  const partes = new Intl.DateTimeFormat(tag, opciones).formatToParts(fecha)
  const dia = partes.find((parte) => parte.type === 'day')?.value ?? ''
  const mes = partes.find((parte) => parte.type === 'month')?.value ?? ''
  const anio = partes.find((parte) => parte.type === 'year')?.value ?? ''
  return `${dia} de ${capitalizar(mes, tag)} del ${anio}`
}

/**
 * Fecha y hora completas, en el idioma vigente.
 *
 * Para los `title` que dan el dato exacto detrás de un texto aproximado —«se
 * borra en 3 días», y al pasar por encima el día y la hora—. Se escribía a mano
 * con `toLocaleString('es-MX')`, que es de las cosas que ningún linterna de
 * traducción ve: no es texto, es un idioma clavado en el código.
 */
export function longDateTime(value: string | number | Date, timeZone?: string): string {
  return new Date(value).toLocaleString(locale(), {
    ...(timeZone ? { timeZone } : {}),
    dateStyle: 'long',
    timeStyle: 'short',
  })
}

/** `Sábado, 29 de Agosto del 2026` · `Saturday, August 29, 2026`. */
export function longDateWithWeekday(value: string | number | Date, timeZone?: string): string {
  const tag = locale()
  const fecha = new Date(value)
  if (!tag.startsWith('es')) {
    return new Intl.DateTimeFormat(tag, {
      ...(timeZone ? { timeZone } : {}),
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(fecha)
  }

  const dia = new Intl.DateTimeFormat(tag, {
    ...(timeZone ? { timeZone } : {}),
    weekday: 'long',
  }).format(fecha)
  return `${capitalizar(dia, tag)}, ${longDate(value, timeZone)}`
}

/**
 * `29 de Agosto` · `August 29`, sin año.
 *
 * Para lo que ocurre dentro del año en curso —una gráfica, un periodo de
 * facturación—, donde repetir el año en cada etiqueta es ruido.
 */
export function longDateNoYear(value: string | number | Date, timeZone?: string): string {
  const tag = locale()
  const opciones: Intl.DateTimeFormatOptions = {
    ...(timeZone ? { timeZone } : {}),
    day: 'numeric',
    month: 'long',
  }
  const fecha = new Date(value)
  if (!tag.startsWith('es')) return new Intl.DateTimeFormat(tag, opciones).format(fecha)

  const partes = new Intl.DateTimeFormat(tag, opciones).formatToParts(fecha)
  const dia = partes.find((parte) => parte.type === 'day')?.value ?? ''
  const mes = partes.find((parte) => parte.type === 'month')?.value ?? ''
  return `${dia} de ${capitalizar(mes, tag)}`
}

/** `Martes` · `Tuesday`, solo. Para cuando el día y la fecha van en renglones distintos. */
export function weekdayIn(value: string | number | Date, timeZone?: string): string {
  const tag = locale()
  const dia = new Intl.DateTimeFormat(tag, {
    ...(timeZone ? { timeZone } : {}),
    weekday: 'long',
  }).format(new Date(value))
  /* En inglés `Intl` ya devuelve «Tuesday» con su mayúscula; forzarla no cambia
     nada aquí, pero sí en un idioma que escriba los días en minúscula a
     propósito, así que sólo se toca donde se sabe que hace falta. */
  return tag.startsWith('es') ? capitalizar(dia, tag) : dia
}
